import { Injectable } from '@angular/core';
import {
  FeatureValidation,
  MyOrganizationFacade,
  ProjectsFacade,
  getRolesAndPermission,
  isFeatureEnabled,
  validateAndAlert,
} from '@piqet-angular/data';
import {
  adminRoleStr,
  defaultColumn,
  DELETE_RECORD_LABEL,
  FeatureCodes,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
  SUPER_ADMIN_DELETE_RECORD_LABEL,
  superAdminRoleStr,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { MyOrganizationService } from 'libs/data/src/lib/services/my-organization/my-organization.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { cloneDeep } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject, catchError, finalize, of, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrganizationPreferencesDataGridService {
  FeatureCodes = FeatureCodes;
  litterImpactPayload: any;
  suppliersListPayload: any;
  customCategoryPayload: any;
  event: any;
  Access: any;
  resetCustomOrganizationWeighting = new Subject<{ indicatorId: number }>();
  resetCustomOrganizationWeighting$ = this.resetCustomOrganizationWeighting.asObservable();

  editInlineData = new Subject<[any, any, any, any, any]>();
  inlinePrefObservable$ = this.editInlineData.asObservable();

  deleteOrganizationMap = new Subject<any>();
  deleteOrganizationMapData$ = this.deleteOrganizationMap.asObservable();

  updateBookMarkPreference = new Subject<[any]>();
  updateBookMarkPreference$ = this.updateBookMarkPreference.asObservable();
  constructor(
    private myOrganizationFacade: MyOrganizationFacade,
    private myOrganizationService: MyOrganizationService,
    private toaster: ToastrService,
    private swalService: SwalService,
    private sharedService: SharedService,
    private projectsFacade: ProjectsFacade
  ) {}

  getLitterImpactDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'organization',
      headerName: 'Organization',
      field: 'organizationName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      sortable: false,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'litterImpact',
      headerName: 'Litter impact',
      field: 'isLitterLeakageImpact',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        editData: (data: any) => {
          this.myOrganizationFacade.openLitterImpactModal(data, 'Review custom speedos');
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  editLitterImpactDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'packagingLevelName',
      headerName: 'Packaging level',
      field: 'packagingLevelName',
      wrapHeaderText: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'landfill',
      headerName: 'Landfill (%)',
      field: 'landfill',
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      cellRendererParams: {
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'recycling',
      headerName: 'Recycling (%)',
      field: 'recycling',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'homeComposting',
      headerName: 'Home composting (%)',
      field: 'homeComposting',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'commercialComposting',
      headerName: 'Commercial composting (%)',
      field: 'commercialComposting',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      lockPosition: right,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'incineration',
      headerName: 'Incineration (%)',
      field: 'incineration',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'wasteEnergyRecovery',
      headerName: 'Energy recovery (%)',
      field: 'wasteEnergyRecovery',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'openDump',
      headerName: 'Open dump (%)',
      field: 'openDump',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'anerobicDigestion',
      headerName: 'Anerobic digestion (%)',
      field: 'anerobicDigestion',
      sortable: false,
      wrapHeaderText: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'litter',
      headerName: 'Litter (%)',
      field: 'litter',
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'stormWater',
      headerName: 'Storm water (%)',
      field: 'stormWater',
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'sewer',
      headerName: 'Sewer (%)',
      field: 'sewer',
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'recyclingOpenLoop',
      headerName: 'Recycling, open loop (%)',
      field: 'recyclingOpenLoop',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'chemicalRecycling',
      headerName: 'Chemical recycling (%)',
      field: 'chemicalRecycling',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'pyrolysis',
      headerName: 'Pyrolysis/Gasification (%)',
      field: 'pyrolysis',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'openBurning',
      headerName: 'Open burning (%)',
      field: 'openBurning',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'none',
      headerName: 'None (%)',
      field: 'none',
      wrapHeaderText: true,
      sortable: false,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inputRenderer',
      cellRendererParams: {
        showEdit: true,
        updateData: (data: any) => {
          this.myOrganizationFacade.updateLitterImpactData(data);
        },
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      autoHeaderHeight: true,
      resizable: false,
    });

    return colDef;
  }

  getYearEndsOnDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'organization',
      headerName: 'Organization',
      field: 'organizationName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      sortable: false,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'YEAR ENDS On',
      headerName: 'Year ends on',
      field: 'organizationYearEndDate',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        showDelete: true,
        value: 'organizationYearEndOn',
        deleteData: (data: any, customName: any) => {
          this.myOrganizationFacade.openMyOrgDeleteModal(
            null,
            '/MyOrganizationPreferences/DeleteOrganizationEndYear',
            customName
          );
        },
        editData: (data: any) => {
          this.myOrganizationFacade.openEditOrgYearEndModal(
            data.id,
            'Review organization year end'
          );
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getSuppliersListDataColumn(roleName: string): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'Id',
      headerName: 'Id',
      field: 'Id',
      hide: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Name',
      headerName: 'Supplier name',
      field: 'name',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        showDelete:
          roleName.toLowerCase() == superAdminRoleStr || roleName.toLowerCase() == adminRoleStr
            ? true
            : false,
        value: 'listOfSuppliers',
        deleteData: (data: any) => {
          this.deleteSupplierData(data.id, roleName.toLowerCase() == superAdminRoleStr);
        },
        editData: (data: any) => {
          this.myOrganizationFacade.openAddEditSupplierModal(data.id, 'Review supplier');
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }
  getBookmarkDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'componentName',
      headerName: 'Component name',
      field: 'componentName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'scenarioName',
      headerName: 'Scenario name',
      field: 'scenarioName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'projectName',
      headerName: 'Project name',
      field: 'projectName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        isBookMarkGrid: true,
        bookMarkComponent: (data: any) => {
          this.updateBookMarkPreference.next(data);
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getCustomCategoryDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'organizationName',
      headerName: 'Organization',
      field: 'organizationName',
      // sort: 'desc',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      sortable: false,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'indicators',
      headerName: 'Indicators',
      field: 'indicatorsName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      sortable: false,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: this.isAccess() ? this.isAccess().isEdit : true,
        showDelete: this.isAccess() ? this.isAccess().isDelete : true,
        value: 'customCategory',
        deleteData: (data: any, customName: any) => {
          this.myOrganizationFacade.openMyOrgDeleteModal(
            null,
            '/MyOrganizationPreferences/DeleteOrganizationCustomCategory',
            customName
          );
        },
        editData: (data: any) => {
          this.myOrganizationFacade.openCustomCategoryModal(data, 'Review custom category');
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });

    return colDef;
  }

  getCustomCategoryPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.getCustomCategoryDataColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'actions');

    return (this.customCategoryPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'Name',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize || 10,
      draw: currentPage || 1,
    });
  }

  getCustomSpeedosDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'organizationName',
      headerName: 'Organization',
      field: 'organizationName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      // sort: 'desc',
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'SpeedosName',
      headerName: 'Speedos indicators',
      field: 'speedosName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        editData: (data: any) => {
          this.myOrganizationFacade.openEditSpeedosModal(data, 'Review custom speedos');
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getCustomPackgingMetricsDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'organizationName',
      headerName: 'Organization',
      field: 'organizationName',
      // sort: 'desc',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'PackagingMetricsName',
      headerName: 'Packaging metrics',
      field: 'packagingMetricsName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xxm,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        showDelete: true,
        value: 'customPackagingMaterial',
        deleteData: (data: any, customName: any) => {
          this.myOrganizationFacade.openMyOrgDeleteModal(
            null,
            '/MyOrganizationPreferences/DeleteOrganizationPackagingMetrics',
            customName
          );
        },
        editData: (data: any) => {
          this.myOrganizationFacade.openEditMetricsModal(data, 'Review packaging metrics');
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getOrgWeightingsColumns(): ColDef[] {
    const colDef: ColDef[] = [];
    let enableEditing: any;
    let resetData: any;

    colDef.push({
      ...defaultColumn,
      colId: 'IndicatorName',
      headerName: 'LCA indicators',
      field: 'indicatorName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Unit',
      headerName: 'Unit',
      field: 'unit',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Normalization',
      headerName: 'Normalization',
      field: 'normalization',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      filter: 'agNumberColumnFilter',
      resizable: false,
      cellRenderer: (params: any) => {
        return this.toScientificNotation(params);
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Weighting',
      headerName: 'Weighting (%)',
      field: 'weighting',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: true,
      filter: 'agNumberColumnFilter',
      resizable: false,
      cellRenderer: (params: any) => {
        return this.toFormattedNumber(params, 'weighting');
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'DefaultValue',
      headerName: 'Custom weightings (%)',
      field: 'defaultValue',
      wrapText: true,
      autoHeight: true,
      sortable: false,
      lockVisible: false,
      resizable: false,
      filter: 'agNumberColumnFilter',
      editable: true,
      cellEditorParams: {
        from: 'orgWeightings',
        allowZero: 'true',
      },
      cellClass: ['outerGridEdit', 'outerGridOrgEdit', 'elipsisFields'],
      cellRenderer: (params: any) => {
        const rawValue = !params.data.defaultValue ? 0 : params.data.defaultValue;
        return `<span class="editable-icon editable-icon-padding">
        ${this.toFormattedNumber({ value: rawValue })}
        <i class="fa fa-pencil icon-opacity" aria-hidden="true"></i>
        </span>`;
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'defaultValue') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        const oldValue = params.oldValue;
        params = { ...params };
        params.data = { ...params.data };
        const isNumber = /^[0-9]+(\.[0-9]+)?$/.test(newVal);

        if (valueChanged) {
          if (isNumber) {
            const parsedValue = parseFloat(newVal);

            if (parsedValue < 0 || parsedValue > 100) {
              this.toaster.error('Custom weighting must be between 0 and 100');
              params.data[params.colDef.field] = oldValue;
              params.api.refreshCells({
                rowNodes: [params.node],
                columns: [params.colDef.field],
                force: true,
              });
              return false;
            }

            const field = params.colDef.field;
            params.data[field] = typeof params?.oldValue == 'number' ? parsedValue : parsedValue;

            const validations: FeatureValidation[] = [
              {
                condition: true,
                featureCode: this.FeatureCodes.PREFERENCES_ORGANIZATION_WEIGHTINGS,
                featureName: 'Organization weightings',
              },
            ];

            const hasPermission = (featureCode: string) =>
              isFeatureEnabled(FeatureCodes.PREFERENCES_ORGANIZATION_WEIGHTINGS);

            if (!validateAndAlert(validations, hasPermission, this.swalService)) {
              return false;
            }

            this.myOrganizationFacade.updateWeightingsList(params.data, 'edit', null, field);
            this.saveOrgWeightingsData();
            return true;
          } else {
            this.toaster.error('Custom weighting is required and must be a number');
            params.data[params.colDef.field] = oldValue;
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [params.colDef.field],
              force: true,
            });
            return false;
          }
        }
        return false;
      },
    });

    // New Reset Column
    colDef.push({
      ...defaultColumn,
      colId: 'ResetAction',
      headerName: 'Actions',
      field: '',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      sortable: false,
      filter: false,
      resizable: false,
      lockVisible: false,
      suppressMenu: true,
      cellClass: 'reset-action-cell',
      cellRenderer: (params: any) => {
        // Only show reset icon if there's a custom value different from default
        const hasCustomValue = params.data?.defaultValue;
        if (hasCustomValue != null && hasCustomValue >= 0) {
          return `<span class="reset-icon-wrapper" title="Reset to PIQET default weighting">
                  <i class="fa fa-undo reset-weighting-icon icon-opacity" style="cursor: pointer;position:absolute; right:30px !important;" aria-hidden="true"></i>
                </span>`;
        }
        return '';
      },
      onCellClicked: (params: any) => {
        if (
          params.event.target.closest('.reset-weighting-icon') ||
          params.event.target.closest('.reset-icon-wrapper')
        ) {
          this.resetWeightingValue(params);
        }
        this.myOrganizationFacade.resetWeightingsList(params.data, 'reset');
        return true;
      },
    });

    return colDef;
  }

  getOrganizationColData(): ColDef[] {
    let materialName: any;
    let materialId: any;
    let conversionName: any;
    let conversionId: any;
    let enableEditing: any;
    let resetData: any;
    let isCallApi: boolean = false;

    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'Name',
      headerName: 'Name',
      field: 'name',
      width: 468,
      valueSetter: (params: any) => {
        const newVal = params.newValue.trim() ?? '';
        const oldVal = params.oldValue;
        const valueChanged = !resetData && (oldVal !== newVal || newVal == '');
        isCallApi = false;
        if (valueChanged) {
          isCallApi = true;
          params.data = { ...params.data };
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number'
              ? parseFloat(newVal)
              : newVal == ''
              ? oldVal
              : newVal;
          if (newVal == '') {
            localStorage.setItem('checkEmptyNameOrganization', 'Empty');
          }
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.colId;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionForOrganization$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.organizationPreferencesDataMapProcess(
                  params.data,
                  'edit',
                  colId,
                  rowId
                );
            });
          return true;
        }
        return false;
      },
      wrapText: true,
      autoHeight: true,
      resizable: false,
      editable: true,
      filter: 'agTextColumnFilter',
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'piqetMaterial',
      headerName: 'PIQET Material',
      field: 'materialName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'organizationMaterialName',
        editData: (data: any) => {
          materialName = data.materialName;
          materialId = data.materialId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text-input-quality" title="${params.data.materialName}">${
          params.data.materialName ? params.data.materialName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!materialName ? materialName : 'Select process';
        const valueChanged =
          !!materialName && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['materialId'] = materialId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          isCallApi = true;
          this.projectsFacade.checkUpdateActionForOrganization$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.organizationPreferencesDataMapProcess(
                  params.data,
                  'edit',
                  colId,
                  rowId
                );
            });

          materialName = '';
          materialId = '';
        }

        return valueChanged;
      },
      valueGetter: params => {
        return params.data.materialName;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'piqetConversion',
      headerName: 'PIQET Conversion',
      field: 'conversionName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'organizationConversionName',
        editData: (data: any) => {
          conversionName = data.conversionName;
          conversionId = data.conversionId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text-input-quality" title="${params.data.conversionName}">${
          params.data.conversionName ? params.data.conversionName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!conversionName ? conversionName : 'Select process';
        const valueChanged =
          !!conversionName && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged || isCallApi) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['conversionId'] = valueChanged ? conversionId : params.data['conversionId'];
          params.data['conversionName'] = valueChanged
            ? conversionName
            : params.node.data['conversionName'];
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionForOrganization$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.organizationPreferencesDataMapProcess(
                  params.data,
                  'edit',
                  colId,
                  rowId
                );
            });

          conversionName = '';
          conversionId = '';
          isCallApi = false;
          localStorage.removeItem('isCheckOrganizationMapExist');
          localStorage.removeItem('checkEmptyNameOrganization');
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.conversionName;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'organizationPreferencesDataMapping',
        inlineEditData: (data: any) => {
          enableEditing = data.data.enableEditing;
          resetData = data.data.resetData;
        },
        deleteInlineGrid: (data: any) => {
          this.alertConfirmation(data);
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.organizationPreferencesDataMapProcess(data, 'delete');
        this.deleteOrganizationMap.next(data);
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }

  isAccess = () => {
    return getRolesAndPermission(
      rolesModuleName.myOrganizationCustomCategory,
      mainModuleName.myOrganizationModule
    );
  };

  saveOrgWeightingsData() {
    const dataStr: any = localStorage.getItem('editedWeighting');
    const payload = JSON.parse(dataStr);
    this.myOrganizationService
      .editCustomWeightings(payload)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        )
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  // Reset function to handle reset button click
  resetWeightingValue(params: any): void {
    const indicatorId = this.getIndicatorId(params);

    if (!indicatorId) {
      this.toaster.error('Unable to identify indicator for reset');
      return;
    }

    // Check permissions before resetting
    const validations: FeatureValidation[] = [
      {
        condition: true,
        featureCode: this.FeatureCodes.PREFERENCES_ORGANIZATION_WEIGHTINGS,
        featureName: 'Organization weightings',
      },
    ];

    const hasPermission = (featureCode: string) =>
      isFeatureEnabled(FeatureCodes.PREFERENCES_ORGANIZATION_WEIGHTINGS);

    if (!validateAndAlert(validations, hasPermission, this.swalService)) {
      return;
    }

    this.resetCustomOrganizationWeighting.next({ indicatorId: Number(indicatorId) });
  }

  getIndicatorId(params: any): string | null {
    // Method 1: Direct property access
    if (params.data?.indicatorId) {
      return params.data.indicatorId;
    }
    return null;
  }
  toScientificNotation(params: any): string {
    const value = Number(params?.value);

    if (isNaN(value)) {
      throw new Error('Invalid input. Please provide a valid number.');
    }

    if (value === 0) {
      return '0';
    }

    let scientificNotation = '';

    if (Math.abs(value).toString().length >= 12) {
      scientificNotation = value.toExponential().toUpperCase();
    } else {
      scientificNotation = value.toString().toUpperCase();
    }

    const [coefficient, exponent] = scientificNotation.split(/e/i);

    if (!exponent) {
      return scientificNotation;
    }

    // Convert coefficient to a string to remove trailing zeros
    const cleanedCoefficient = parseFloat(coefficient).toString();

    // Add leading zero after the dash in the exponent if needed  OUTPUT --> 2.60091E-05
    //   const cleanedExponent = exponent.replace(/(-)(\d)/, '$10$2');

    // Construct the string with the coefficient, '×10^', and exponent OUTPUT --> 2.60091×10^−5
    const powerOfTenNotation = `${cleanedCoefficient} × 10 <sup><small>${exponent}</small></sup>`;

    return powerOfTenNotation;
  }

  toFormattedNumber(params: any, type?: string): string {
    let formattedValue = Number(params?.value);
    if (type) {
      formattedValue = formattedValue * 100;
    }
    const value = formattedValue;
    if (isNaN(value)) {
      throw new Error('Invalid input. Please provide a valid number.');
    }
    if (Math.abs(value) < 0.00005) {
      return '0';
    }
    if (Number.isInteger(value)) {
      return value.toString();
    }
    return value.toFixed(2);
  }

  deleteSupplierData(data: any, isSuperAdmin: boolean) {
    this.swalService.confirm('Are you sure?', SUPER_ADMIN_DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.sharedService.deleteSupplier(data);
      }
    });
  }
}
